import { useId, useRef, useState, type KeyboardEvent } from 'react'
import { Icon } from './icons/Icon'
import './InfoBalloon.css'

export interface InfoBalloonProps {
  /** Short heading shown at the top of the balloon (e.g. the section's own name). */
  title: string
  /** 1–3 plain-language sentences — what this section is, and its one genuine quirk. */
  body: string
  /** Accessible name for the trigger button. Defaults to `About ${title}`. */
  label?: string | undefined
  /** Panel horizontal anchor — flips to `end` near the right edge of a container. */
  align?: 'start' | 'end' | undefined
}

/**
 * A contextual help affordance ("info balloon") for a major section heading — roadmap #12. A real
 * `<button>` (never a `<span>` masquerading as one) that reveals a short title + body on hover OR
 * focus/click, closes on Esc/blur/mouse-leave, and never traps focus (no dialog, no focus-trap —
 * this is a disclosure, not a modal, so Tab simply continues past the trigger to whatever is next).
 *
 * Distinct from `ColumnHeaderInfo` (a per-column, click-only `Popover` dialog used inside `DataGrid`
 * headers) — this is the coarser, section-level affordance the UX charter calls for, so it opens on
 * hover too (a demo viewer scanning a screen shouldn't have to click every icon) and is wired as a
 * true tooltip (`role="tooltip"` + `aria-describedby`) rather than a non-modal dialog.
 */
export function InfoBalloon({ title, body, label, align = 'start' }: InfoBalloonProps) {
  const [hoverOpen, setHoverOpen] = useState(false)
  const [focusOpen, setFocusOpen] = useState(false)
  const [clickOpen, setClickOpen] = useState(false)
  const [escDismissed, setEscDismissed] = useState(false)
  const tooltipId = useId()
  const triggerRef = useRef<HTMLButtonElement>(null)

  const open = (hoverOpen || focusOpen || clickOpen) && !escDismissed

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === 'Escape' && open) {
      event.stopPropagation()
      setEscDismissed(true)
    }
  }

  return (
    <span
      className="iw-info-balloon"
      onMouseEnter={() => {
        setHoverOpen(true)
        setEscDismissed(false)
      }}
      onMouseLeave={() => setHoverOpen(false)}
    >
      <button
        ref={triggerRef}
        type="button"
        className="iw-info-balloon__trigger"
        aria-label={label ?? `About ${title}`}
        aria-describedby={open ? tooltipId : undefined}
        onFocus={() => {
          setFocusOpen(true)
          setEscDismissed(false)
        }}
        onBlur={() => {
          setFocusOpen(false)
          setClickOpen(false)
        }}
        onClick={() => {
          // A plain assignment, never a toggle: some browsers (notably desktop Safari) do not
          // focus a `<button>` on mouse click at all, so click has to be able to open the balloon
          // on its own rather than only riding along on `onFocus`. The only ways to CLOSE an
          // already-open balloon are Esc, blur, or mouse-leave — never a second click while it is
          // still focused/hovered (that would fight the "still focused" case above and immediately
          // re-close what focus alone would keep open).
          setClickOpen(true)
          setEscDismissed(false)
        }}
        onKeyDown={handleKeyDown}
      >
        <Icon name="info" size={13} />
      </button>
      {open ? (
        <span
          id={tooltipId}
          role="tooltip"
          className={`iw-info-balloon__panel iw-info-balloon__panel--${align}`}
        >
          <span className="iw-info-balloon__title">{title}</span>
          <span className="iw-info-balloon__body">{body}</span>
        </span>
      ) : null}
    </span>
  )
}

/** Alias — some call sites think of this as a "help hint" rather than a "balloon"; same component. */
export const HelpHint = InfoBalloon
